// What went wrong signing in, in TUNE's words: Cognito's errors, as a person reads them.
import { CognitoError } from './cognito';

/** What a step was doing, for the words that fit it. */
export type Doing = 'signIn' | 'signUp' | 'code' | 'reset' | 'newPassword' | 'authenticator' | 'changePassword';

/** The pool's password rule, as the stand-in and Cognito say it. */
function passwordWords(message: string, least: number): string {
  if (/long enough|length/i.test(message)) {
    return `That password is too short: use at least ${least} characters.`;
  }
  return `That password isn't allowed: use at least ${least} characters.`;
}

export function authProblem(e: unknown, doing: Doing, least = 10): string {
  if (!(e instanceof CognitoError)) {
    return 'Something went wrong. Try again.';
  }
  switch (e.type) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      if (doing === 'changePassword') {
        return "Your current password isn't right.";
      }
      if (doing === 'newPassword' || doing === 'authenticator' || doing === 'code') {
        return /session/i.test(e.message)
          ? 'That took too long. Sign in again to carry on.'
          : "That didn't work. Sign in again to carry on.";
      }
      return "That email and password don't match a TUNE account.";
    case 'UsernameExistsException':
      return 'There’s already a TUNE account with this email. Sign in, or reset its password.';
    case 'InvalidPasswordException':
      return passwordWords(e.message, least);
    case 'CodeMismatchException':
      return doing === 'authenticator'
        ? "That code isn't right. Check the time on your phone is set automatically, then give the code it shows now."
        : "That code isn't right. Check the email we sent, and try again.";
    case 'EnableSoftwareTokenMFAException':
      return "That code isn't right. Check the time on your phone is set automatically, then give the code it shows now.";
    case 'ExpiredCodeException':
      return 'That code has expired. Ask for a new one.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'That was a lot of tries. Wait a few minutes, then try again.';
    case 'InvalidParameterException':
      return /email/i.test(e.message)
        ? 'Check the email address.'
        : 'Something in the form isn’t right. Check it, and try again.';
    case 'NetworkError':
      return "TUNE Analytics couldn't reach its sign-in. Check the connection, and try again.";
    case 'UserNotConfirmedException':
      return 'Confirm your email first: we sent you a code.';
    case 'PasswordResetRequiredException':
      return 'TUNE asked for a new password. Reset it to carry on.';
    default:
      return 'Something went wrong. Try again in a minute.';
  }
}
